import React, { useState } from 'react';

import { useHistory } from 'react-router-dom';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { MY_LISTINGS } from '../../../../../../utils/routes';

import { ConfirmationStep } from './ConfirmationStep';
import { ReasonExclusionStep } from './ReasonExclusionStep';
import { Modal } from '../../../../../components/Modal';
import { FeedbackRatingStep } from './FeedbackRatingStep';
import { getCurrentContent } from './utils/get-current-content';
import { deleteAd, getDeletingStatus, sendFeedback } from '../../../../../services/Ad.service';
import toastify from '../../../../../lib/toastify';
import { getErrorMessage, getSuccessMessage } from './utils/get-messages';
import {
  CurrentModalView,
  DeactivateModalProps,
  DeleteAd,
  FeedbackContentOnError
} from './types/modal-deactivate';
import { DELETE_STATUS_QUERY, LISTING_QUERY, SEND_FEEDBACK_MUTATION } from '../../../../../queries';

export function Deactivate({
  modalOpen,
  setModalOpen,
  adId,
  isDelete
}: DeactivateModalProps): JSX.Element {
  const [selectedRadio, setSelectedRadio] = useState<number>();
  const [currentModalView, setCurrentModalView] = useState<CurrentModalView>('confirmation');
  const [feedbackContentOnError, setFeedbackContentOnError] = useState<
    FeedbackContentOnError | undefined
  >();
  const { hookType, defaultValue, endpoint } = getCurrentContent(!!isDelete);

  const queryClient = useQueryClient();

  const history = useHistory();

  const deleteStatus = useQuery({
    queryFn: getDeletingStatus,
    queryKey: [DELETE_STATUS_QUERY],
    enabled: hookType !== 0
  });

  const feedbackMutation = useMutation({
    mutationFn: ({ selectedOption, feedback }: { selectedOption: string; feedback?: string }) => {
      return sendFeedback(selectedOption, feedback);
    },
    mutationKey: [SEND_FEEDBACK_MUTATION],
    onError: (_, { selectedOption, feedback }) => {
      setFeedbackContentOnError({
        selectedOption,
        feedback
      });
      setCurrentModalView('error');
    },
    onSuccess: () => setCurrentModalView('success')
  });

  const deleteMutation = useMutation({
    mutationFn: ({ id, status }: DeleteAd) => {
      return deleteAd(id, status);
    },
    onSuccess: (_, { type }) => {
      const { title, content } = getSuccessMessage(type);
      if (type === 'delete') {
        setModalOpen(false);
        queryClient.invalidateQueries({ queryKey: [LISTING_QUERY] });
        history.push(MY_LISTINGS);
      } else {
        setCurrentModalView('feedback');
      }
      toastify.success(title, content);
    },
    onError: (_, { type }) => {
      const { title, content } = getErrorMessage(type);
      toastify.error(title, content);
    }
  });

  function handleDelete() {
    const selectedRadioOrDefaultValue = selectedRadio ?? defaultValue;

    if (selectedRadioOrDefaultValue < 0) return;

    deleteMutation.mutate({
      id: adId,
      status: selectedRadioOrDefaultValue,
      type: endpoint
    });
  }

  function handleFeedback(selectedOption: string, feedback?: string) {
    feedbackMutation.mutate({
      selectedOption,
      feedback
    });
  }

  function handleRetry() {
    if (!feedbackContentOnError) return;

    feedbackMutation.mutate({
      selectedOption: feedbackContentOnError.selectedOption,
      feedback: feedbackContentOnError?.feedback
    });
  }

  function handleClose() {
    queryClient.invalidateQueries({ queryKey: [LISTING_QUERY] });

    setModalOpen(false);
  }

  if (currentModalView === 'confirmation') {
    return (
      <ConfirmationStep
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        handleDelete={handleDelete}
        loading={deleteStatus.isFetching}
        isDelete={!!isDelete}
        setCurrentModalView={setCurrentModalView}
        isDeleteLoading={deleteMutation.isLoading}
      />
    );
  }

  if (currentModalView === 'reason') {
    return (
      <ReasonExclusionStep
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        handleDelete={handleDelete}
        loading={deleteStatus.isLoading}
        content={deleteStatus.data}
        selectedRadio={selectedRadio}
        setSelectedRadio={setSelectedRadio}
        isDeleteLoading={deleteMutation.isLoading}
      />
    );
  }

  if (currentModalView === 'feedback') {
    return (
      <FeedbackRatingStep
        modalOpen={modalOpen}
        handleClose={handleClose}
        handleFeedback={handleFeedback}
        loading={feedbackMutation.isLoading}
      />
    );
  }

  if (currentModalView === 'success') {
    return <Modal.Success handleClose={handleClose} modalOpen={modalOpen} />;
  }

  return (
    <Modal.Error
      handleClose={handleClose}
      modalOpen={modalOpen}
      handleRetry={handleRetry}
      loading={feedbackMutation.isLoading}
    />
  );
}
